import React from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, ArrowLeft, Lightbulb, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../../components/layout/PublicFooter'
import { PublicNavigation } from '../../components/layout/PublicNavigation'
import { Button } from '../../components/ui/Button'

export const BestPracticesPage: React.FC = () => {
  const practices = [
    {
      title: 'Start Early',
      description: 'Begin planning your 1031 exchange well before closing on your relinquished property. This gives you time to identify replacement properties and complete all required documentation.',
      icon: Lightbulb
    },
    {
      title: 'Keep Detailed Records',
      description: 'Maintain comprehensive records of all transactions, communications, and documents. This will help ensure compliance and make the process smoother.',
      icon: Lightbulb
    },
    {
      title: 'Work with Qualified Professionals',
      description: 'Consult with qualified intermediaries, tax advisors, and real estate professionals who have experience with 1031 exchanges.',
      icon: Lightbulb
    },
    {
      title: 'Understand the Rules',
      description: 'Familiarize yourself with IRS Section 1031 requirements, including the 45-day identification period and 180-day exchange period.',
      icon: Lightbulb
    },
    {
      title: 'Use Like-Kind Properties',
      description: 'Ensure your replacement property qualifies as "like-kind" to your relinquished property. Most real estate qualifies, but there are exceptions.',
      icon: Lightbulb
    },
    {
      title: 'Avoid Receiving Boot',
      description: 'To fully defer taxes, ensure the value of your replacement property equals or exceeds the value of your relinquished property, and reinvest all proceeds.',
      icon: Lightbulb
    }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/documentation" className="inline-flex items-center text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Documentation
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <BookOpen className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Best Practices
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Tips and strategies for successful 1031 exchanges from industry experts.
            </p>
          </div>
        </div>
      </section>

      {/* Practices Grid */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {practices.map((practice, index) => {
                const Icon = practice.icon
                return (
                  <div
                    key={index}
                    className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-[#69adba]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                          {practice.title}
                        </h3>
                        <p className="text-slate-600 dark:text-slate-400">
                          {practice.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Have Questions?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Our support team and AI assistant Neela are here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/support">
              <Button className="bg-white text-[#69adba] hover:bg-slate-50">
                Contact Support
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/documentation">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                View All Documentation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

